import { computed, toRaw, onScopeDispose } from 'vue'
import pLimit from 'p-limit'
import { useQueryClient } from '@tanstack/vue-query'
import { useQuery, useMutation } from './api'
import { useUserSettings } from './useUserSettings'
import { useCreditorSettingsStore } from '../stores/creditorSettingsStore'
import { useAccess } from './useAccess'
import { useAuth } from './useAuth'
import { useDebounceFn, useEventListener } from '@vueuse/core'
import { deepMerge, deepSet } from 'ts-deep-utils'
import type { components } from '../types/_api'

type CreditorSettings = components['schemas']['Creditor-Settings']
type CreditorSettingsData = CreditorSettings['settings']
type EmailNotificationType = 'tickets' | 'decisions' | 'activities'

const FLUSH_DELAY_MS = 5000

interface CreditorSelection {
  all_selected?: boolean
  included?: { creditor: string }[]
  excluded?: { creditor: string }[]
}

function buildCreditorClause(
  creditorSelection: CreditorSelection | undefined
): { creditor: { $in: string[] } | { $nin: string[] } } | undefined {
  if (creditorSelection == null) {
    return undefined
  }

  const { all_selected, included, excluded } = creditorSelection
  if (all_selected === true) {
    const excludedIds = (excluded ?? []).map((item) => item.creditor)
    if (excludedIds.length === 0) {
      return undefined
    }
    return { creditor: { $nin: excludedIds } }
  } else {
    const includedIds = (included ?? []).map((item) => item.creditor)
    return { creditor: { $in: includedIds } }
  }
}

function getCreditorWhereClause(
  creditorSelection: CreditorSelection | undefined,
  selectedTagIds?: string[]
):
  | {
      'creditor'?: { $in: string[] } | { $nin: string[] }
      'creditor_tags.creditor_tag'?: { $in: string[] }
    }
  | undefined {
  const creditorClause = buildCreditorClause(creditorSelection)
  const tagClause =
    selectedTagIds != null && selectedTagIds.length > 0
      ? { 'creditor_tags.creditor_tag': { $in: selectedTagIds } }
      : undefined

  if (creditorClause == null && tagClause == null) return undefined
  return { ...creditorClause, ...tagClause }
}

// Consumers of the cached settings map:
//   - StatisticsCards reads `dashboard_statistics.hidden` per creditor to
//     decide which stat cards to render. The counts/trends inside those
//     cards come from `POST /dashboard--statistics-summary` server-side.
//   - SidebarSettings, StatisticsSettingsDialog, NotificationSettingsDialog
//     read + mutate the settings docs.
// TodoCard and EventsCard only use `hasSelection` / `creditorWhereClause`
// (derived from userSettings, not from the settings map) and the per-creditor
// `notification_types` filter is applied server-side in
// `POST /dashboard--todo-notifications` — they do not depend on the cached
// settings map.
export function useCreditorSettings() {
  const creditorSettingsStore = useCreditorSettingsStore()
  const access = useAccess()

  const { settings: userSettings } = useUserSettings()

  const hasSelection = computed(() => {
    const selection = userSettings.value.dashboard?.creditor_selection
    if (selection == null) return false
    if (selection.all_selected) return true

    const includedCount = selection.included?.length ?? 0
    return includedCount > 0
  })

  const isAllSelected = computed(() => {
    return userSettings.value.dashboard?.creditor_selection?.all_selected === true
  })

  const creditorWhereClause = computed(() => {
    const tagIds = (userSettings.value.dashboard?.creditor_tag_selection ?? []).map((item) => item.creditor_tag)
    return getCreditorWhereClause(userSettings.value.dashboard?.creditor_selection, tagIds)
  })

  const { isSolicitor } = useAuth()

  // Solicitors don't see settings-driven UI; skip the fetch to avoid
  // pulling thousands of unused docs.
  const shouldFetchSettings = computed(() => !isSolicitor.value)

  const creditorSettingsQuery = useQuery(
    '/creditor--settings',
    {
      // No `where` clause — backend access-control scopes to the caller's
      // accessible docs. Filter toggles filter client-side via
      // `selectedCreditorIds` instead of re-fetching.
      getAll: true,
    },
    {
      enabled: shouldFetchSettings,
    }
  )

  // Skip per-mutation auto-invalidation so bulk saves don't trigger N full-list
  // refetches; we invalidate once via `scheduleSettingsInvalidation` (debounced
  // for single toggles) or directly after `saveCreditorSettingsImmediately`.
  const updateCreditorSettingsMutation = useMutation('patch', '/creditor--settings/{resource_id}', {
    meta: { notifyOnSuccess: false, skipAutoInvalidation: true },
  })

  const createCreditorSettingsMutation = useMutation('post', '/creditor--settings', {
    meta: { notifyOnSuccess: false, skipAutoInvalidation: true },
  })

  const queryClient = useQueryClient()

  // Both dashboard aggregation endpoints read creditor--settings server-side,
  // so settings mutations must invalidate them too.
  async function invalidateSettingsQueries() {
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const path = query.queryKey[1]
        if (typeof path !== 'string') return false
        return (
          path.startsWith('/creditor--settings') ||
          path.startsWith('/dashboard--statistics-summary') ||
          path.startsWith('/dashboard--todo-notifications')
        )
      },
    })
  }

  // 500 ms coalesces a burst of per-creditor toggles into one refetch.
  const scheduleSettingsInvalidation = useDebounceFn(invalidateSettingsQueries, 500)

  const serverCreditorSettingsMap = computed(() => {
    const map = new Map<string, CreditorSettings>()
    for (const item of creditorSettingsQuery.data.value?._items ?? []) {
      map.set(item.creditor, item)
    }
    return map
  })

  // Creditors currently visible in the dashboard, resolved client-side from
  // the cached settings docs against `creditor_selection` + `creditor_tag_selection`.
  // The denormalized `creditor_tags` field on each doc powers tag filtering.
  const selectedCreditorIds = computed<string[]>(() => {
    const selection = userSettings.value.dashboard?.creditor_selection
    const tagSelection = userSettings.value.dashboard?.creditor_tag_selection ?? []
    const selectedTagIds = new Set(tagSelection.map((item) => item.creditor_tag))

    if (selection?.all_selected === true) {
      const excluded = new Set((selection.excluded ?? []).map((item: { creditor: string }) => item.creditor))
      const matches: string[] = []
      for (const [id, doc] of serverCreditorSettingsMap.value) {
        if (excluded.has(id)) continue
        if (selectedTagIds.size > 0) {
          // Legacy settings docs (created before the creditor_tags cascade was
          // added) don't carry the field — treat them as having no tags so an
          // active tag filter excludes them rather than crashing. The type says
          // creditor_tags is always present (schema default: []), but legacy
          // docs predate that default.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          const tagRefs = doc.creditor_tags ?? []
          if (!tagRefs.some((ref) => selectedTagIds.has(ref.creditor_tag))) continue
        }
        matches.push(id)
      }
      return matches
    }
    return (selection?.included ?? []).map((item) => item.creditor)
  })

  function serverCreditorSettings(creditorId: string): CreditorSettings | undefined {
    return serverCreditorSettingsMap.value.get(creditorId)
  }

  function settings(creditorId: string): CreditorSettingsData {
    const serverSettings = serverCreditorSettings(creditorId)
    const raw = toRaw(serverSettings?.settings ?? {})

    // Clone only when a patch needs to be merged. Callers are read-only,
    // so the no-patch dashboard hot path returns the raw server snapshot.
    const pendingPatch = creditorSettingsStore.pendingSettings.get(creditorId)
    if (pendingPatch != null) {
      return deepMerge(structuredClone(raw), pendingPatch)
    }
    const uncommittedPatch = creditorSettingsStore.getCreditorUncommittedSettings(creditorId)
    if (uncommittedPatch != null) {
      return deepMerge(structuredClone(raw), uncommittedPatch)
    }

    return raw
  }

  async function saveCreditorSettings(creditorId: string) {
    if (!creditorSettingsStore.hasCreditorUncommittedSettings(creditorId)) {
      return
    }

    const patchBody = creditorSettingsStore.getCreditorUncommittedSettings(creditorId)
    if (patchBody == null) {
      return
    }

    if (creditorSettingsStore.pendingSettings.has(creditorId)) {
      return
    }

    const serverSettings = serverCreditorSettings(creditorId)

    if (serverSettings != null) {
      const canWrite = await access.canWriteAttributeAsync('creditor--settings', 'settings')
      if (!canWrite) {
        return
      }
    }

    creditorSettingsStore.pendingSettings.set(creditorId, patchBody)
    creditorSettingsStore.clearCreditorUncommittedSettings(creditorId)

    try {
      if (serverSettings == null) {
        await createCreditorSettingsMutation.mutateAsync({
          // @ts-expect-error Swagger lies - creditor and settings are valid fields for POST
          body: {
            creditor: creditorId,
            settings: patchBody,
          },
        })
      } else {
        await updateCreditorSettingsMutation.mutateAsync({
          path: { resource_id: serverSettings._id },
          header: { 'If-Match': serverSettings._etag },
          // @ts-expect-error Swagger lies - settings is a valid field for PATCH
          body: {
            settings: patchBody,
          },
        })
      }
      void scheduleSettingsInvalidation()
    } catch {
      creditorSettingsStore.mergeCreditorUncommittedSettings(creditorId, patchBody)
    } finally {
      creditorSettingsStore.pendingSettings.delete(creditorId)
    }
  }

  const debouncedSaveMap = new Map<string, ReturnType<typeof useDebounceFn>>()

  function getOrCreateDebouncedSave(creditorId: string) {
    let debouncedFn = debouncedSaveMap.get(creditorId)
    if (debouncedFn == null) {
      debouncedFn = useDebounceFn(() => saveCreditorSettings(creditorId), FLUSH_DELAY_MS)
      debouncedSaveMap.set(creditorId, debouncedFn)
    }
    return debouncedFn
  }

  onScopeDispose(() => {
    void saveCreditorSettingsImmediately()
  })

  useEventListener(window, 'beforeunload', () => {
    void saveCreditorSettingsImmediately()
  })

  function updateSetting(creditorId: string, dottedPath: string, value: unknown) {
    const patch: Record<string, unknown> = {}
    deepSet(patch, dottedPath, value)
    creditorSettingsStore.mergeCreditorUncommittedSettings(creditorId, patch)
    void getOrCreateDebouncedSave(creditorId)()
  }

  function isUserSubscribedToEmail(
    creditorId: string,
    notificationType: EmailNotificationType,
    userId: string
  ): boolean {
    const creditorSettingsData = settings(creditorId)
    const emailSettings = creditorSettingsData.user_notifications?.email
    const typeSettings = emailSettings == null ? null : emailSettings[notificationType]

    if (typeSettings == null) return true

    const userIds = typeSettings.users.map((u) => u.user)
    if (typeSettings.settings_type === 'include') {
      return userIds.includes(userId)
    }
    return !userIds.includes(userId)
  }

  function updateUserNotificationSubscription(
    creditorId: string,
    notificationType: EmailNotificationType,
    userId: string,
    subscribed: boolean
  ) {
    const creditorSettingsData = settings(creditorId)
    const emailSettings = creditorSettingsData.user_notifications?.email ?? {}
    const typeSettings = emailSettings[notificationType] ?? {
      settings_type: 'exclude' as const,
      users: [] as { user: string }[],
    }

    const userIndex = typeSettings.users.findIndex((u) => u.user === userId)
    const userExists = userIndex > -1
    const isInclude = typeSettings.settings_type === 'include'

    // No-op short-circuit. Callers may iterate over every creditor on each
    // toggle; skipping unchanged entries here keeps bulk toggling cheap.
    const wantsInUsers = isInclude ? subscribed : !subscribed
    if (wantsInUsers === userExists) return

    const users = [...typeSettings.users]
    if (wantsInUsers) {
      users.push({ user: userId })
    } else {
      users.splice(userIndex, 1)
    }

    updateSetting(creditorId, `user_notifications.email.${notificationType}`, {
      settings_type: typeSettings.settings_type,
      users,
    })
  }

  async function saveCreditorSettingsImmediately(): Promise<void> {
    const creditorIdsToSave = creditorSettingsStore.getAllCreditorIdsWithUncommittedSettings()
    if (creditorIdsToSave.length === 0) return

    // HTTP/2 multiplexes the requests; backend write throughput is the bottleneck.
    const limit = pLimit(50)
    await Promise.all(creditorIdsToSave.map((id) => limit(() => saveCreditorSettings(id))))

    // Flush immediately rather than waiting on the debounce — the dashboard
    // would otherwise show stale data for the rest of the debounce window.
    await invalidateSettingsQueries()
  }

  // Accumulating bulk path for dialogs that commit many per-creditor patches at
  // once. Caller flushes via `saveCreditorSettingsImmediately`.
  function bulkUpdateSettings(operations: readonly { creditorId: string; dottedPath: string; value: unknown }[]): void {
    if (operations.length === 0) return

    const patchesByCreditor = new Map<string, Record<string, unknown>>()
    for (const { creditorId, dottedPath, value } of operations) {
      let patch = patchesByCreditor.get(creditorId)
      if (patch == null) {
        patch = {}
        patchesByCreditor.set(creditorId, patch)
      }
      deepSet(patch, dottedPath, value)
    }

    for (const [creditorId, patch] of patchesByCreditor) {
      creditorSettingsStore.mergeCreditorUncommittedSettings(creditorId, patch)
    }
  }

  function isLoadingCreditor(creditorId: string): boolean {
    return creditorSettingsQuery.isLoading.value && serverCreditorSettings(creditorId) == null
  }

  function isPendingCreditor(creditorId: string): boolean {
    return creditorSettingsStore.pendingSettings.has(creditorId)
  }

  return {
    selectedCreditorIds,
    creditorWhereClause,
    hasSelection,
    isAllSelected,
    isLoading: creditorSettingsQuery.isLoading,
    isLoadingCreditor,
    isPending: computed(() => updateCreditorSettingsMutation.isPending.value),
    isPendingCreditor,

    settings,
    serverCreditorSettings,

    updateSetting,
    bulkUpdateSettings,
    saveCreditorSettingsImmediately,

    isUserSubscribedToEmail,
    updateUserNotificationSubscription,
  }
}
